import CheckIcon from '@mui/icons-material/Check';
import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';

export interface FundStatusStepperProps<T extends string> {
  statuses: T[];
  activeStatus: T;
  labelMap?: Partial<Record<T, string>>;
  canPromote: boolean;
  onPromote: (toStatus: T) => void;
}

function StatusConnector({ completed }: { completed: boolean }) {
  return (
    <Box
      sx={{
        flex: 1,
        height: '2px',
        mt: '15px',
        minWidth: 16,
        backgroundColor: completed ? Palette.default.buttonBg : Palette.default.border,
      }}
    />
  );
}

function StatusCircle({
  completed,
  active,
  index,
}: {
  completed: boolean;
  active: boolean;
  index: number;
}) {
  const emphasized = completed || active;
  return (
    <Box
      className="fund-step-circle"
      sx={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: emphasized ? Palette.default.buttonBg : '#FFFFFF',
        border: `2px solid ${emphasized ? 'transparent' : Palette.default.border}`,
        color: emphasized ? '#FFFFFF' : Palette.default.disabledText,
        fontSize: '0.8125rem',
        fontWeight: 700,
        boxShadow: active ? `0 0 0 4px ${Palette.default.activeBg}` : 'none',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        flexShrink: 0,
      }}
    >
      {completed ? <CheckIcon sx={{ fontSize: 18 }} /> : index + 1}
    </Box>
  );
}

function FundStatusStep<T extends string>({
  status,
  index,
  currentIndex,
  last,
  label,
  canPromote,
  onPromote,
}: {
  status: T;
  index: number;
  currentIndex: number;
  last: boolean;
  label: string;
  canPromote: boolean;
  onPromote: (toStatus: T) => void;
}) {
  const isCompleted = index < currentIndex;
  const isActive = index === currentIndex;
  const isClickable = index === currentIndex + 1 && canPromote;
  const labelColor = isActive
    ? Palette.default.buttonBg
    : isCompleted
      ? Palette.default.text
      : Palette.default.disabledText;

  return (
    <React.Fragment>
      <Tooltip title={isClickable ? `Promote to ${label}` : ''} disableHoverListener={!isClickable}>
        <Box
          onClick={() => isClickable && onPromote(status)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 88,
            cursor: isClickable ? 'pointer' : 'default',
            '&:hover .fund-step-circle': isClickable
              ? {
                  borderColor: Palette.default.buttonBg,
                  boxShadow: `0 0 0 4px ${Palette.default.activeBg}`,
                }
              : {},
          }}
        >
          <StatusCircle completed={isCompleted} active={isActive} index={index} />
          <Typography
            sx={{
              mt: 1,
              fontSize: '0.75rem',
              fontWeight: isActive ? 700 : 500,
              color: labelColor,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Tooltip>
      {!last && <StatusConnector completed={isCompleted} />}
    </React.Fragment>
  );
}

function FundStatusStepper<T extends string>({
  statuses,
  activeStatus,
  labelMap = {},
  canPromote,
  onPromote,
}: FundStatusStepperProps<T>) {
  const currentIndex = statuses.indexOf(activeStatus);
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
      {statuses.map((status, index) => (
        <FundStatusStep
          key={status}
          status={status}
          index={index}
          currentIndex={currentIndex}
          last={index === statuses.length - 1}
          label={(labelMap as Record<T, string>)[status] || status}
          canPromote={canPromote}
          onPromote={onPromote}
        />
      ))}
    </Box>
  );
}

export default FundStatusStepper;
